<script setup lang="ts">
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import EventCreate from '@/components/features/events/EventCreate.vue'
import { computed, defineProps, ref, watch } from 'vue'
import EventEdit from '@/components/features/events/EventEdit.vue'
import type { Event } from '@/api-swagger'
import { useEventStore } from '@/stores/useEventStore'
import { storeToRefs } from 'pinia'
import { dateComparator } from '@/utils/dateUtils'
import EventGridActions from '@/components/features/events/EventGridActions.vue'
import { type LocationQueryValue, useRoute } from 'vue-router'
const $route = useRoute()

// Récupération des paramètres de requête depuis l'URL

const queryDate = computed(() => {
  if ($route.query.date) {
    return $route.query.date
  }
  return null
})

const queryCategory = computed(() => {
  if ($route.query.category) {
    return $route.query.category
  }
  return null
})

const gridApi = ref<any>(null)

defineProps({
  showAddButton: {
    type: Boolean,
    default: true,
  },
  filter: {
    type: Boolean,
    default: true,
  },
})

const eventStore = useEventStore()
const { eventsUserList } = storeToRefs(eventStore)
const selectedEvent = ref<Event | null>(null)
const showModal = ref(false)
const eventsUserListWithPartnerName = ref<Event[]>([])

function openModal(event: Event) {
  selectedEvent.value = event
  showModal.value = true
}

watch(
  eventsUserList,
  async (newEvents) => {
    if (newEvents && newEvents.length) {
      eventsUserListWithPartnerName.value = newEvents.map((event: Event) => ({
        ...event,
        partnerName: event.partnerSelected?.name || '',
      }))
    }
  },
  { immediate: true },
)

watch(
  [queryCategory, queryDate],
  ([newCategory, newDate]) => {
    if (gridApi.value) {
      const filterModel = buildFilterModel(newCategory, newDate)
      gridApi.value.setFilterModel(filterModel)
    }
  },
  { immediate: true },
)

function closeModal() {
  selectedEvent.value = null
  showModal.value = false
}

function buildFilterModel(
  category: string | LocationQueryValue[] | null,
  date: string | LocationQueryValue[] | null,
) {
  const filterModel: Record<string, any> = {}

  if (category) {
    filterModel.category = {
      filterType: 'text',
      type: 'contains',
      filter: category,
    }
  }

  if (date) {
    filterModel.start = {
      filterType: 'text',
      type: 'contains',
      filter: date,
    }
  }

  return filterModel
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onGridReady(params: any) {
  gridApi.value = params.api
  if (queryCategory.value || queryDate.value) {
    const filterModel = buildFilterModel(queryCategory.value, queryDate.value)
    params.api.setFilterModel(filterModel)
  }
}
</script>

<template>
  <EventCreate v-if="showAddButton" />
  <grid-vue
    @openModal="openModal"
    @gridReady="onGridReady"
    :list="eventsUserListWithPartnerName"
    :cols="[
      { field: 'name', headerName: 'Sujet', flex: 3, minWidth: 200 },
      {
        field: 'start',
        headerName: 'Date',
        // filter: 'agDateColumnFilter',
        flex: 2,
        minWidth: 200,
        comparator: dateComparator,
      },
      { field: 'partnerName', headerName: 'Société', flex: 2, minWidth: 200 },
      { field: 'category', headerName: 'Catégorie', minWidth: 200 },
      { field: 'author', headerName: 'Auteur', minWidth: 200 },
      {
        field: 'actions',
        headerName: 'Actions',
        resizable: false,
        filter: false,
        minWidth: 100,
        maxWidth: 100,
        cellRenderer: EventGridActions,
        cellRendererParams: {
          onClick: (params: any) => {
            openModal(params as Event)
          },
          tooltipPosition: 'left',
        },
        sortable: false,
        cellClass: 'border-0 grid-action ',
      },
    ]"
  ></grid-vue>
  <EventEdit
    v-if="showModal && selectedEvent"
    :eventId="selectedEvent?.id"
    @close="closeModal"
    :showModal="showModal"
  />
</template>
